import Button from '@alltrails/shared/denali/components/Button';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import Close from '@alltrails/shared/icons/Close';
import PortalModal from 'components/shared/PortalModal/PortalModal';
import useIsMobile from '@alltrails/shared/hooks/useIsMobile';
import { useIntl } from '@alltrails/shared/react-intl';
import Typography from '@alltrails/shared/denali/components/Typography';
import StarRating from '@alltrails/shared/denali/components/StarRating';
import InstallPromptType from '@alltrails/analytics/enums/InstallPromptType';
import InstallPromptLocation from '@alltrails/analytics/enums/InstallPromptLocation';
import InstallPromptSource from '@alltrails/analytics/enums/InstallPromptSource';
import { useEffect } from 'react';
import logInstallPromptViewed from '@alltrails/analytics/events/logInstallPromptViewed';
import logInstallPromptDismissed from '@alltrails/analytics/events/logInstallPromptDismissed';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import * as styles from './styles/styles.module.scss';
import appIconSrc from '../../../../assets/images/icons/app_icon.png';
import useDeeplink from '../hooks/useDeeplink';
import { deepLinkRedirect, isAndroid } from '../utils';

type BranchBannerProps = {
  promptLocation: InstallPromptLocation;
  pageId?: number;
  pageType?: 'area' | 'trail';
  isOpen?: boolean;
  onRequestClose?: () => void;
  displayTrigger?: CarouselDisplayTrigger;
};

// Design link: https://www.figma.com/file/eQkMBkn2YnhYkVahhaXrZG/In-House-Branch?type=design&node-id=728-29259&mode=design&t=PL0roJqI0OHiLtzf-0

const BranchBanner = ({ pageId, pageType, promptLocation, isOpen = false, displayTrigger, onRequestClose }: BranchBannerProps) => {
  const isMobile = useIsMobile();
  const intl = useIntl();
  const deeplink = useDeeplink(pageType, pageId);

  useEffect(() => {
    if (isOpen && isMobile) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: InstallPromptType.Banner,
        install_prompt_source: InstallPromptSource.Native,
        signup_modal_trigger: displayTrigger
      });
    }
  }, [displayTrigger, isOpen, promptLocation, isMobile]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: InstallPromptType.Banner,
      install_prompt_source: InstallPromptSource.Native
    });
    onRequestClose();
  };

  return (
    <PortalModal isOpen={isOpen && isMobile}>
      <div className={styles.container}>
        <div className={styles.contentContainer}>
          <IconButton
            className={styles.close}
            icon={{ Component: Close }}
            onClick={handleCloseClick}
            size="sm"
            title={intl.formatMessage({ defaultMessage: 'Close' })}
            testId="close-button"
            variant="flat"
          />
          <img src={appIconSrc} alt="" />
          <div className={styles.nameAndRatingContainer}>
            <Typography variant="heading200">AllTrails</Typography>
            <div className={styles.ratingContainer}>
              <StarRating rating={5} variant="complex" />
              <Typography variant="text100" component="span">
                ({isAndroid() ? 4.8 : 4.9})
              </Typography>
            </div>
          </div>
        </div>
        <Button
          allowWrap
          text={intl.formatMessage({ defaultMessage: 'Get app' })}
          onClick={() => deepLinkRedirect(deeplink, InstallPromptType.Banner, promptLocation)}
          className={styles.button}
          testId="banner-button"
          variant="primary"
          size="md"
        />
      </div>
    </PortalModal>
  );
};

export default BranchBanner;
